import React, { useState, useEffect, useRef } from "react";
import { Drawer, Button, Space, Input, message } from "antd";
import { DownloadOutlined, CodeOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import DocPreview from "./DocPreview";
import { fetchFullDoc } from "./api";

const DOC_CANVAS_WIDTH = 794;
const FIT_PREVIEW_STYLE = { height: "100%", minHeight: 0, flex: "1 1 auto" };

const pdfPreviewUrl = (url) => {
  if (!url) return "";
  return `${url}${url.includes("#") ? "&" : "#"}toolbar=0&navpanes=0&scrollbar=0&view=Fit&zoom=page-fit`;
};

const FitPdfPreview = ({ url }) => (
  <div style={{ ...FIT_PREVIEW_STYLE, overflow: "hidden", background: "#f5f5f5" }}>
    <object
      data={pdfPreviewUrl(url)}
      type="application/pdf"
      width="100%"
      height="100%"
      style={{ display: "block", width: "100%", height: "100%", border: 0 }}
    >
      PDF non disponibile
    </object>
  </div>
);

const FitHtmlPreview = ({ id }) => {
  const frameRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const frame = frameRef.current;
    const content = contentRef.current;
    if (!frame || !content) return undefined;

    let raf = null;
    const updateScale = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const frameWidth = Math.max(frame.clientWidth - 32, 1);
        const frameHeight = Math.max(frame.clientHeight - 32, 1);
        const contentWidth = content.scrollWidth || DOC_CANVAS_WIDTH;
        const contentHeight = content.scrollHeight || 1;
        const nextScale = Math.min(frameWidth / contentWidth, frameHeight / contentHeight, 1);
        setScale(Number.isFinite(nextScale) && nextScale > 0 ? nextScale : 1);
      });
    };

    const observer = new ResizeObserver(updateScale);
    observer.observe(frame);
    observer.observe(content);
    updateScale();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [id]);

  return (
    <div
      ref={frameRef}
      style={{
        ...FIT_PREVIEW_STYLE,
        width: "100%",
        overflow: "hidden",
        padding: 16,
        boxSizing: "border-box",
        background: "#f5f5f5",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <div
        ref={contentRef}
        style={{
          width: DOC_CANVAS_WIDTH,
          padding: 24,
          boxSizing: "border-box",
          background: "#fff",
          transform: `scale(${scale})`,
          transformOrigin: "top center",
        }}
      >
        <DocPreview id={id} />
      </div>
    </div>
  );
};

// Drawer di ingrandimento: anteprima del documento + azioni per tipo
// (PDF → scarica; HTML → vedi/copia sorgente).
const DocumentDrawer = ({ doc, onClose, actions = null }) => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const [source, setSource] = useState(null); // null = anteprima, string = sorgente HTML
  const [loadingSource, setLoadingSource] = useState(false);

  const isPdf = !!(doc && doc.pdf_form && doc.pdf_form.url);

  // Torna all'anteprima quando si cambia documento.
  useEffect(() => {
    setSource(null);
  }, [doc]);

  const showSource = async () => {
    if (!doc) return;
    setLoadingSource(true);
    try {
      const token = await getTokenSilently();
      const full = await fetchFullDoc(token, env, doc.objectId);
      setSource(full?.documento || "");
    } catch (e) {
      console.error("Errore caricamento sorgente:", e);
      message.error("Errore nel caricamento del sorgente");
    } finally {
      setLoadingSource(false);
    }
  };

  const copySource = () => {
    navigator.clipboard.writeText(source || "");
    message.success("Sorgente copiato negli appunti");
  };

  return (
    <Drawer
      title={doc?.nome_documento || "Documento"}
      open={!!doc}
      onClose={onClose}
      width="70%"
      destroyOnClose
      styles={{ body: { padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 } }}
      extra={
        <Space>
          {actions}
          {isPdf ? (
            <a href={doc.pdf_form.url} target="_blank" rel="noopener noreferrer" download>
              <Button icon={<DownloadOutlined />}>Scarica PDF</Button>
            </a>
          ) : (
            <Button icon={<CodeOutlined />} loading={loadingSource} onClick={showSource}>
              Sorgente
            </Button>
          )}
          <Button onClick={onClose}>Chiudi</Button>
        </Space>
      }
    >
      {doc &&
        (source !== null ? (
          <div style={{ ...FIT_PREVIEW_STYLE, overflow: "auto", padding: 16 }}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Space>
                <Button type="primary" onClick={copySource}>Copia sorgente</Button>
                <Button onClick={() => setSource(null)}>Torna all'anteprima</Button>
              </Space>
              {source ? (
                <Input.TextArea
                  value={source}
                  readOnly
                  autoSize={{ minRows: 20, maxRows: 40 }}
                  style={{ fontFamily: "monospace", fontSize: 12 }}
                />
              ) : (
                <span style={{ color: "#888" }}>Questo documento non ha sorgente HTML.</span>
              )}
            </Space>
          </div>
        ) : isPdf ? (
          // Il PDF usa l'altezza residua reale del Drawer e il viewer viene aperto in fit-page.
          <FitPdfPreview url={doc.pdf_form.url} />
        ) : (
          // Documento HTML scalato per restare interamente dentro l'area visibile.
          <FitHtmlPreview id={doc.objectId} />
        ))}
    </Drawer>
  );
};

export default DocumentDrawer;
